import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import { DadosMedico } from 'components';
import { usePublicLayout, useUi } from 'hooks';
import { api } from 'services';
import { token as storage, files as fileActions } from 'utils';

import { MedicoAuditor } from 'components/Cid10List/Cid10List';

type ResponseProps = {
  dataResposta: string;
  id: string;
  perguntaId: string;
  resposta: string;
};

type Pendencies = {
  idBeneficiario: string;
  nomeBeneficiario: string;
  perguntaId: number;
  liberarAnexo: boolean;
  pergunta: string;
  doencaId: number;
  idDoenca: string;
  titulo: string;
  especificacaoTitulo: string;
  tipo: string;
  tempo?: string;
  local?: string;
  grau?: string;
  medicoAuditor: MedicoAuditor;
  altura: string;
  peso: string;
};

type RespostaType = { resposta: string };

type FormValues = {
  respostas: RespostaType[];
};

const checkIfFilesAreTooBig = (files: File[] | null): boolean => {
  if (!files) return true;
  return files.every(file => file.size / 1024 / 1024 <= 5);
};

const checkIfFilesAreCorrectType = (files: File[] | null): boolean => {
  if (!files) return true;
  return files.every(file => file.type === 'application/pdf');
};

const MIN_LENGTH_INPUT_TEXT = 5;
const MAX_LENGTH_INPUT_TEXT = 300;

const schema = yup.object().shape({
  respostas: yup.array().of(
    yup.object().shape({
      resposta: yup.string().test(
        'resposta-or-file',
        'Este campo é obrigatório se não houver um arquivo anexado',
        // eslint-disable-next-line func-names
        function (value) {
          const { path } = this;
          const index = parseInt(path.match(/\d+/)?.[0] || '-1', 10);
          const hasFiles =
            this.options.context?.inputFiles?.[index]?.length > 0;

          return (
            hasFiles ||
            (value && value.trim().length >= MIN_LENGTH_INPUT_TEXT) ||
            false
          );
        }
      ),
    })
  ),
});

const Response: React.FC = () => {
  const [pedencies, setPedencies] = useState<Pendencies[]>([]);
  const [inputFiles, setInputFiles] = useState<File[][]>([]);
  const [linkIsValid, setLinkIsValid] = useState(false);
  const [medicoAuditor, setMedicoAuditor] = useState<MedicoAuditor | null>(
    null
  );
  const inputFilesRef = useRef<HTMLInputElement[]>([]);
  const { errorMessage, successMessage, showBackdrop, hideBackdrop } = useUi();
  const { isAuthenticated } = usePublicLayout();
  const { link } = useParams();
  const navigate = useNavigate();
  const { control, formState, handleSubmit, reset, trigger } =
    useForm<FormValues>({
      mode: 'onSubmit',
      resolver: yupResolver(schema),
      //  context: { hasFile: inputFiles.length > 0 },
      context: { inputFiles },
      defaultValues: {
        respostas: [],
      },
    });
  const { fields, append } = useFieldArray({ control, name: 'respostas' });

  const fetchPedencies = useCallback(() => {
    const url = `${process.env.REACT_APP_MS_AUDITORIA_URL}/perguntas/listar-perguntas-link/${link}`;
    const headers = { Authorization: `Bearer ${storage.getAccessToken()}` };

    api
      .get(url, { headers })
      .then(({ data }) => {
        if (Array.isArray(data) && data.length === 0) {
          navigate('/link-invalido');
        }
        setPedencies(data);
      })
      .catch(err => {
        if (err.response.status === 404) {
          navigate('/link-invalido');
        }
        if (err.response.status === 500) {
          navigate('/error');
        }
      });
  }, [link, navigate]);

  useEffect(() => {
    if (linkIsValid) {
      hideBackdrop();
    } else {
      showBackdrop();
    }
    return () => hideBackdrop();
  }, [hideBackdrop, linkIsValid, showBackdrop]);

  useEffect(() => {
    if (pedencies.length) {
      hideBackdrop();
    } else {
      showBackdrop();
    }
  }, [hideBackdrop, pedencies, showBackdrop]);

  useEffect(() => {
    const validaLink = (): void => {
      const url = `${process.env.REACT_APP_MS_AUDITORIA_URL}/link/${link}`;
      const headers = { Authorization: `Bearer ${storage.getAccessToken()}` };

      api
        .get(url, { headers })
        .then(({ data }) => {
          if (data.isValid) {
            setLinkIsValid(true);
          } else {
            navigate('/link-invalido');
          }
        })
        .catch(() => {
          navigate('/rejeitada');
        });
    };

    if (isAuthenticated) {
      validaLink();
    }
  }, [link, navigate, isAuthenticated]);

  useEffect(() => {
    if (linkIsValid) {
      fetchPedencies();
    }
  }, [fetchPedencies, linkIsValid]);

  useEffect(() => {
    if (pedencies.length > 0) {
      setMedicoAuditor(pedencies[0].medicoAuditor);
    }
  }, [pedencies]);

  useEffect(() => {
    const pedenciesLength = pedencies.length;
    let fieldsLength = fields.length;

    for (fieldsLength; fieldsLength < pedenciesLength; fieldsLength += 1) {
      append({ resposta: '' });
    }
  }, [append, fields.length, pedencies.length]);

  useEffect(() => {
    fields.forEach((_, index) => {
      trigger(`respostas.${index}.resposta`);
    });
  }, [inputFiles, fields, trigger]);

  const handleChangeInputFiles = (
    filesList: FileList | null,
    index: number
  ): void => {
    let isValid = true;
    const newFiles = filesList ? Array.from(filesList) : [];
    const currentFiles = inputFiles[index] || [];
    const allFiles = [...currentFiles, ...newFiles].filter(
      (file, i, arr) =>
        arr.findIndex(f => f.name === file.name && f.size === file.size) === i
    );

    if (!checkIfFilesAreCorrectType(allFiles)) {
      errorMessage('A extensão do arquivo deve ser somente PDF (.pdf)');
      isValid = false;
    }
    if (!checkIfFilesAreTooBig(allFiles)) {
      errorMessage('O tamanho do arquivo excedeu o tamanho máximo de 5MB');
      isValid = false;
    }

    if (isValid) {
      const currentInputFiles = [...inputFiles];
      currentInputFiles[index] = allFiles;
      setInputFiles(currentInputFiles);
      trigger(`respostas.${index}.resposta`);
      return;
    }

    if (inputFilesRef.current[index]) {
      inputFilesRef.current[index].value = '';
    }
  };

  const handleRemoveFile = (fileIndex: number, inputIndex: number): void => {
    const updatedFiles = [...inputFiles];
    updatedFiles[inputIndex] = updatedFiles[inputIndex].filter(
      (_, idx) => idx !== fileIndex
    );
    setInputFiles(updatedFiles);
    trigger(`respostas.${inputIndex}.resposta`);
  };
  const clearForm = (): void => {
    reset();
    setInputFiles([]);
    fetchPedencies();

    if (inputFilesRef.current) {
      inputFilesRef.current = [];
    }
  };

  const onSubmit: SubmitHandler<FormValues> = async ({ respostas }) => {
    const url = `${process.env.REACT_APP_MS_AUDITORIA_URL}/respostas/v3`;
    const headers = {
      Authorization: `Bearer ${storage.getAccessToken()}`,
      'Content-Type': 'application/json',
    };

    try {
      const payloads = await Promise.all(
        respostas.map(async (item, index) => {
          const { perguntaId } = pedencies[index];
          const files = inputFiles[index] || [];

          const documentos = await Promise.all(
            files.map(async file => ({
              documento: await fileActions.convertFileToBase64(file),
              mimeType: file.type,
              nomeDocumento: file.name,
              tamanhoDocumento: file.size,
            }))
          );

          return {
            documentos,
            perguntaId,
            resposta: item.resposta,
          };
        })
      );
      await api.post(url, payloads, { headers });

      successMessage('Resposta(s) enviada(s) com sucesso!');
      navigate('/registrada');
    } catch (err: any) {
      if (err?.response?.status === 400) {
        errorMessage(err.response.data.errorMessage[0].message);
      }
      navigate('/rejeitada');
    } finally {
      console.log('HIDE');
    }
  };
  if (!linkIsValid) {
    return null;
  }

  return (
    <Grid container sx={{ '& > :not(style)': { mb: 2 } }}>
      <Grid item md={12}>
        <Box sx={{ marginBottom: 5 }}>
          <Typography variant="h3">
            Pendências solicitadas pelo médico
          </Typography>
          <Typography variant="subtitle1">
            Confira as informações e responda a cada pergunta relacionada a
            doença preenchida do beneficiário em questão.
          </Typography>
        </Box>
        {medicoAuditor && (
          <Paper sx={{ marginBottom: 5, padding: '1rem 2rem 2rem' }}>
            <Box>
              <Typography fontWeight={600}>
                Médico auditor responsável
              </Typography>
              <DadosMedico
                nome={medicoAuditor.nome}
                crm={medicoAuditor.crm}
                especialidade={medicoAuditor.especialidade}
              />
            </Box>
          </Paper>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          {fields.map((item, index) => {
            const {
              nomeBeneficiario,
              titulo,
              especificacaoTitulo,
              tipo,
              tempo,
              local,
              grau,
              liberarAnexo,
              pergunta,
              altura,
              peso,
            } = pedencies[index];

            return (
              <Paper
                key={item.id}
                sx={{ marginBottom: 5, padding: '1rem 2rem 2rem' }}
              >
                <Box>
                  <Typography>
                    <b>Nome:</b> {nomeBeneficiario}
                  </Typography>
                  {titulo && (
                    <Typography>
                      <b>Categoria:</b> {titulo}
                    </Typography>
                  )}
                  {especificacaoTitulo && (
                    <Typography>
                      <b>Doença:</b> {especificacaoTitulo}
                    </Typography>
                  )}
                  {tipo && (
                    <Typography>
                      <b>Tipo:</b> {tipo}
                    </Typography>
                  )}
                  {tempo && (
                    <Typography>
                      <b>Tempo:</b> {tempo}
                    </Typography>
                  )}
                  {local && (
                    <Typography>
                      <b>Local:</b> {local}
                    </Typography>
                  )}
                  {grau && (
                    <Typography>
                      <b>Grau:</b> {grau}
                    </Typography>
                  )}
                  {altura && (
                    <Typography>
                      <b>Altura:</b> {altura} m
                    </Typography>
                  )}
                  {peso && (
                    <Typography>
                      <b>Peso:</b> {peso} Kg
                    </Typography>
                  )}
                </Box>
                <Grid item xs={12} sx={{ pt: 1 }}>
                  <Controller
                    control={control}
                    name={`respostas.${index}.resposta`}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        variant="standard"
                        label={pergunta}
                        inputProps={{ maxLength: MAX_LENGTH_INPUT_TEXT }}
                        InputLabelProps={{
                          sx: { whiteSpace: 'normal', position: 'relative' },
                        }}
                        error={
                          !!formState.errors?.respostas?.[index]?.resposta &&
                          !!formState.touchedFields?.respostas?.[index]
                            ?.resposta
                        }
                        helperText={
                          formState.touchedFields?.respostas?.[index]
                            ?.resposta &&
                          formState.errors?.respostas?.[index]?.resposta
                            ? formState.errors?.respostas[index]?.resposta
                                ?.message || ''
                            : ``
                        }
                        onChange={e => {
                          field.onChange(e); // Atualiza o valor do campo
                          trigger(`respostas.${index}.resposta`); // Força a validação em tempo real
                        }}
                      />
                    )}
                  />
                </Grid>

                {liberarAnexo && (
                  <Grid item xs={12} sx={{ pt: 1 }}>
                    <Box>
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor={`file-input-${index}`}>
                        <Button
                          variant="outlined"
                          component="span"
                          sx={{
                            color: '#666666',
                            borderColor: '#666688',
                            padding: '2px 8px',
                            '&:hover': {
                              borderColor: 'blue',
                              backgroundColor: 'rgba(0, 0, 255, 0.1)',
                            },
                          }}
                        >
                          Anexar arquivos
                        </Button>
                      </label>
                      <input
                        id={`file-input-${index}`}
                        ref={ref => {
                          if (ref) {
                            inputFilesRef.current[index] = ref;
                          }
                        }}
                        style={{ display: 'none' }}
                        type="file"
                        accept="application/pdf"
                        onChange={e => {
                          handleChangeInputFiles(e.target.files, index);
                        }}
                        multiple
                      />
                    </Box>
                    <Typography variant="caption">
                      Somente extensões PDF são aceitas. Limite de 5MB por
                      arquivo.
                    </Typography>
                    {inputFiles[index] && inputFiles[index].length > 0 && (
                      <Box
                        sx={{
                          mt: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'inline-block', // só ocupa a largura necessária
                            minWidth: 200,
                            maxWidth: '100%',
                          }}
                        >
                          {inputFiles[index].map((file, fileIdx) => (
                            <Box
                              key={file.name}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: 0.5,
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                gap: 1,
                                px: 1,
                                py: 0.5,
                                backgroundColor: '#f9f9f9',
                              }}
                            >
                              <Typography
                                key={file.name}
                                variant="body2"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  mb: 0.5,
                                  '&:last-child': { mb: 0 },
                                }}
                              >
                                <span
                                  style={{
                                    display: 'inline-block',
                                    width: '8px',
                                    height: '8px',
                                    backgroundColor: '#00995D',
                                    borderRadius: '50%',
                                  }}
                                >
                                  {' '}
                                </span>
                                {file.name}
                              </Typography>
                              <DeleteForeverIcon
                                color="error"
                                onClick={() => handleRemoveFile(fileIdx, index)}
                                sx={{
                                  minWidth: 0,
                                  padding: 0,
                                  marginLeft: 1,
                                  cursor: 'pointer',
                                }}
                                aria-label="Remover arquivo"
                              />
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Grid>
                )}
              </Paper>
            );
          })}
          {fields.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ marginBottom: 5 }}>
              <Button
                variant="outlined"
                type="button"
                disabled={formState.isSubmitting}
                onClick={clearForm}
              >
                Limpar
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={!formState.isValid}
              >
                Enviar
              </Button>
            </Stack>
          )}
        </form>
      </Grid>
    </Grid>
  );
};

export default memo(Response);
